"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  LiveAvatarContextProvider,
  useSession,
} from "../liveavatar";
import { useLiveAvatarContext } from "../liveavatar/context";
import { SessionState } from "@heygen/liveavatar-web-sdk";
import { useDirectMicPlayback } from "../liveavatar/useDirectMicPlayback";
import { apiUrl } from "../lib/api";
import {
  SESSION_PREWARM_LEAD_MS,
  SESSION_ROLLOVER_MS,
} from "../../app/api/secrets";

const SESSION_PREWARM_MS = SESSION_ROLLOVER_MS - SESSION_PREWARM_LEAD_MS;
const RECOVERY_RETRY_MS = 3_000;

type SessionNotice = {
  tone: "loading" | "offline";
  title: string;
  detail: string;
};

// ── Icons ────────────────────────────────────────────────────────────────────

const EndIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ── Direct mic controls ───────────────────────────────────────────────────────

const DirectMicControls: React.FC<{
  sessionReady: boolean;
  onEnd: () => void;
  notice: SessionNotice | null;
  status: ReturnType<typeof useDirectMicPlayback>["status"];
  errorMessage: ReturnType<typeof useDirectMicPlayback>["errorMessage"];
  isStreaming: ReturnType<typeof useDirectMicPlayback>["isStreaming"];
  retry: ReturnType<typeof useDirectMicPlayback>["retry"];
}> = ({
  sessionReady,
  onEnd,
  notice,
  status,
  errorMessage,
  isStreaming,
  retry,
}) => {
  const statusLabel = notice
    ? notice.title
    : !sessionReady
    ? "Waiting for session..."
    : status === "starting"
      ? "Starting mic..."
      : status === "error"
        ? "Mic error"
        : "Direct mic live";
  const ringColor =
    notice?.tone === "offline"
      ? "bg-amber-400 shadow-amber-500/40"
      : status === "error"
      ? "bg-red-500 shadow-red-500/50"
      : isStreaming
        ? "bg-blue-400 shadow-blue-500/40"
        : "bg-white/20";

  return (
    <div className="w-full flex flex-col items-center gap-3">
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/60 select-none">
          {status === "starting" ? (
            <span className="w-2 h-2 rounded-full border border-white/40 border-t-white/80 animate-spin" />
          ) : (
            <span className={`w-2 h-2 rounded-full shadow-md ${ringColor} ${sessionReady ? "animate-pulse" : ""}`} />
          )}
          <span>{statusLabel}</span>
        </div>

        {isStreaming && (
          <div className="flex items-center gap-[3px] h-4">
            {[0.6, 1, 0.7, 1, 0.5].map((h, i) => (
              <span
                key={i}
                className="w-[3px] rounded-full bg-blue-400"
                style={{
                  height: `${h * 100}%`,
                  animation: `micBar 0.8s ease-in-out ${i * 0.12}s infinite alternate`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 w-full max-w-xl">
        <div className="min-h-10 flex items-center px-4 rounded-full bg-white/5 border border-white/10 text-sm text-white/55">
          {notice
            ? notice.detail
            : sessionReady
            ? "Direct mic streams smoothly with a 1 second buffer"
            : "Waiting for session..."}
        </div>

        {status === "error" && !notice && (
          <button
            onClick={retry}
            className="flex-shrink-0 h-12 px-4 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition-all duration-200 border border-white/10"
            title="Retry microphone"
          >
            Retry
          </button>
        )}

        <button
          onClick={onEnd}
          className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500/20 hover:bg-red-500 text-red-400 hover:text-white flex items-center justify-center transition-all duration-200 border border-red-500/30 hover:border-red-500"
          title="End session"
        >
          <EndIcon />
        </button>
      </div>

      {status === "error" && errorMessage && !notice && (
        <p className="text-red-400 text-xs">{errorMessage}</p>
      )}

      <style>{`
        @keyframes micBar {
          from { transform: scaleY(0.4); opacity: 0.6; }
          to   { transform: scaleY(1.0); opacity: 1.0; }
        }
      `}</style>
    </div>
  );
};

// ── Main session ──────────────────────────────────────────────────────────────

type AvatarSlotRole = "active" | "pending" | "retiring";

type AvatarSlot = {
  key: number;
  token: string;
  role: AvatarSlotRole;
  startedAt: number;
};

type SessionReadyHandler = (slotKey: number, session: unknown) => void;
type SessionDisconnectedHandler = (slotKey: number) => void;

const LiveAvatarVideo: React.FC<{
  slotKey: number;
  visible: boolean;
  notice: SessionNotice | null;
  onReady: SessionReadyHandler;
  onDisconnected: SessionDisconnectedHandler;
}> = ({ slotKey, visible, notice, onReady, onDisconnected }) => {
  const { sessionState, isStreamReady, startSession, attachElement } = useSession();
  const { sessionRef } = useLiveAvatarContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const startingRef = useRef(false);
  const readyReportedRef = useRef(false);

  useEffect(() => {
    if (sessionState === SessionState.DISCONNECTED) onDisconnected(slotKey);
  }, [onDisconnected, sessionState, slotKey]);

  useEffect(() => {
    if (isStreamReady && videoRef.current) attachElement(videoRef.current);
  }, [attachElement, isStreamReady]);

  useEffect(() => {
    const sessionReady = sessionState === SessionState.CONNECTED && isStreamReady;
    if (sessionReady && !readyReportedRef.current) {
      readyReportedRef.current = true;
      onReady(slotKey, sessionRef.current);
    }
  }, [isStreamReady, onReady, sessionRef, sessionState, slotKey]);

  useEffect(() => {
    if (sessionState === SessionState.INACTIVE && !startingRef.current) {
      startingRef.current = true;
      startSession()
        .catch(() => onDisconnected(slotKey))
        .finally(() => { startingRef.current = false; });
    }
  }, [onDisconnected, slotKey, startSession, sessionState]);

  const sessionReady = sessionState === SessionState.CONNECTED && isStreamReady;

  return (
    <div
      className={`absolute inset-0 transition-opacity duration-300 ${
        visible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!visible}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        className="w-full h-full object-contain"
        style={{ transform: "translateZ(0)", willChange: "transform" }}
      />

      {visible && (!sessionReady || notice) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          <div className="flex flex-col items-center gap-1 px-6 text-center">
            <p className="text-white/75 text-sm font-medium tracking-wide">
              {notice?.title ?? (sessionState === SessionState.INACTIVE ? "Initializing..." : "Connecting...")}
            </p>
            <p className="text-white/40 text-xs leading-relaxed max-w-sm">
              {notice?.detail ?? "Preparing your assistant..."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const LiveAvatarSessionSlot: React.FC<{
  slot: AvatarSlot;
  mode: "FULL" | "CUSTOM";
  visible: boolean;
  notice: SessionNotice | null;
  onReady: SessionReadyHandler;
  onDisconnected: SessionDisconnectedHandler;
}> = ({ slot, mode, visible, notice, onReady, onDisconnected }) => {
  return (
    <LiveAvatarContextProvider sessionAccessToken={slot.token} mode={mode}>
      <LiveAvatarVideo
        slotKey={slot.key}
        visible={visible}
        notice={visible ? notice : null}
        onReady={onReady}
        onDisconnected={onDisconnected}
      />
    </LiveAvatarContextProvider>
  );
};

export const LiveAvatarSession: React.FC<{
  mode: "FULL" | "CUSTOM";
  sessionAccessToken: string;
  onSessionStopped: () => void;
}> = ({ mode, sessionAccessToken, onSessionStopped }) => {
  const [slots, setSlots] = useState<AvatarSlot[]>(() => [
    {
      key: 0,
      token: sessionAccessToken,
      role: "active",
      startedAt: Date.now(),
    },
  ]);
  const [activeKey, setActiveKey] = useState(0);
  const [sessionReady, setSessionReady] = useState(false);
  const [rolloverError, setRolloverError] = useState<string | null>(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  const nextSlotKeyRef = useRef(1);
  const slotsRef = useRef(slots);
  const rollingRef = useRef(false);
  const userEndingRef = useRef(false);
  const recoveryTimerRef = useRef<number | null>(null);
  const retiringKeysRef = useRef(new Set<number>());
  const sessionTargetRef = useRef<unknown | null>(null);
  const sdkSessionsRef = useRef(new Map<number, unknown>());

  const mic = useDirectMicPlayback(sessionReady, sessionTargetRef);

  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);

  useEffect(() => {
    return () => {
      if (recoveryTimerRef.current) {
        window.clearTimeout(recoveryTimerRef.current);
      }
    };
  }, []);

  const removeSlot = useCallback((slotKey: number) => {
    retiringKeysRef.current.delete(slotKey);
    sdkSessionsRef.current.delete(slotKey);
    setSlots((current) => current.filter((slot) => slot.key !== slotKey));
  }, []);

  const startReplacementSession = useCallback(async (
    options: { showNotice?: boolean } = {},
  ) => {
    if (userEndingRef.current) return;

    if (options.showNotice) {
      setIsRecovering(true);
    }

    if (!isBrowserOnline()) {
      setIsOnline(false);
      setIsRecovering(true);
      return;
    }

    if (
      rollingRef.current ||
      slotsRef.current.some((slot) => slot.role === "pending")
    ) {
      return;
    }

    rollingRef.current = true;
    setRolloverError(null);

    try {
      const res = await fetch(apiUrl("/api/start-session"), { method: "POST" });
      const data = await parseJsonResponse(res);

      if (!res.ok) {
        throw new Error(data.error ?? "Failed to start replacement session");
      }

      setSlots((current) => {
        if (current.some((slot) => slot.role === "pending")) return current;

        return [
          ...current,
          {
            key: nextSlotKeyRef.current++,
            token: data.session_token,
            role: "pending",
            startedAt: Date.now(),
          },
        ];
      });
    } catch (error) {
      setRolloverError("We are reconnecting automatically");
      if (options.showNotice || !sessionReady) {
        setIsRecovering(true);
      }
      if (!recoveryTimerRef.current && !userEndingRef.current) {
        recoveryTimerRef.current = window.setTimeout(() => {
          recoveryTimerRef.current = null;
          void startReplacementSession(options);
        }, RECOVERY_RETRY_MS);
      }
    } finally {
      rollingRef.current = false;
    }
  }, [sessionReady]);

  useEffect(() => {
    setIsOnline(isBrowserOnline());

    const handleOnline = () => {
      setIsOnline(true);
      if (isRecovering || !sessionReady) {
        void startReplacementSession({ showNotice: true });
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      setIsRecovering(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [isRecovering, sessionReady, startReplacementSession]);

  useEffect(() => {
    const activeSlot = slots.find((slot) => slot.key === activeKey);
    if (!activeSlot || slots.some((slot) => slot.role === "pending")) return;

    const elapsed = Date.now() - activeSlot.startedAt;
    const delay = Math.max(SESSION_PREWARM_MS - elapsed, 0);
    const timer = window.setTimeout(() => {
      void startReplacementSession();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [activeKey, slots, startReplacementSession]);

  const handleSessionReady = useCallback(
    (slotKey: number, session: unknown) => {
      sdkSessionsRef.current.set(slotKey, session);

      if (slotKey === activeKey) {
        sessionTargetRef.current = session;
        setSessionReady(true);
        setIsRecovering(false);
        setRolloverError(null);
        return;
      }

      const oldKey = activeKey;
      const oldSession = sdkSessionsRef.current.get(oldKey);

      retiringKeysRef.current.add(oldKey);
      sessionTargetRef.current = session;
      setSessionReady(true);
      setIsRecovering(false);
      setRolloverError(null);
      setActiveKey(slotKey);
      setSlots((current) =>
        current.map((slot) => {
          if (slot.key === slotKey) {
            return { ...slot, role: "active", startedAt: Date.now() };
          }
          if (slot.key === oldKey) {
            return { ...slot, role: "retiring" };
          }
          return slot;
        }),
      );

      window.setTimeout(() => {
        void stopSdkSession(oldSession).finally(() => {
          window.setTimeout(() => removeSlot(oldKey), 1000);
        });
      }, 250);
    },
    [activeKey, removeSlot],
  );

  const handleSessionDisconnected = useCallback(
    (slotKey: number) => {
      if (userEndingRef.current) {
        removeSlot(slotKey);
        return;
      }

      if (retiringKeysRef.current.has(slotKey)) {
        removeSlot(slotKey);
        return;
      }

      if (slotKey !== activeKey) {
        const disconnectedSlot = slotsRef.current.find((slot) => slot.key === slotKey);
        removeSlot(slotKey);
        if (disconnectedSlot?.role === "pending") {
          setIsRecovering(true);
          void startReplacementSession({ showNotice: true });
        }
        return;
      }

      sessionTargetRef.current = null;
      setSessionReady(false);
      setIsRecovering(true);
      void startReplacementSession({ showNotice: true });
    },
    [activeKey, removeSlot, startReplacementSession],
  );

  const handleEnd = useCallback(() => {
    userEndingRef.current = true;
    if (recoveryTimerRef.current) {
      window.clearTimeout(recoveryTimerRef.current);
      recoveryTimerRef.current = null;
    }
    sessionTargetRef.current = null;
    setSessionReady(false);
    setIsRecovering(false);
    for (const session of sdkSessionsRef.current.values()) {
      void stopSdkSession(session);
    }
    onSessionStopped();
  }, [onSessionStopped]);

  const sessionNotice: SessionNotice | null = !isOnline
    ? {
        tone: "offline",
        title: "Connection interrupted",
        detail: "We will reconnect automatically when your internet is back",
      }
    : isRecovering
      ? {
          tone: "loading",
          title: "Reconnecting",
          detail: "Restoring your assistant, please stay on this screen",
        }
      : null;

  return (
    <div className="w-[900px] max-w-full flex flex-col items-center gap-5 py-6 px-4">
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl shadow-black/60">
        {slots.map((slot) => (
          <LiveAvatarSessionSlot
            key={slot.key}
            slot={slot}
            mode={mode}
            visible={slot.key === activeKey}
            notice={sessionNotice}
            onReady={handleSessionReady}
            onDisconnected={handleSessionDisconnected}
          />
        ))}
      </div>

      <div className="w-full">
        <DirectMicControls
          sessionReady={sessionReady}
          onEnd={handleEnd}
          notice={sessionNotice}
          status={mic.status}
          errorMessage={mic.errorMessage ?? rolloverError}
          isStreaming={mic.isStreaming}
          retry={mic.retry}
        />
      </div>
    </div>
  );
};

async function stopSdkSession(session: unknown) {
  const stop = (session as { stop?: () => Promise<void> | void } | null)?.stop;
  if (!stop) return;

  await Promise.resolve(stop.call(session)).catch(() => undefined);
}

async function parseJsonResponse(res: Response) {
  return res.json().catch(() => ({ error: "Unable to prepare the session" }));
}

function isBrowserOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}
